// Linter and typescript checking are ignored here because theming types
// need to be fixed and the task is too large to fix it fast
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck
import React, { ReactNode } from 'react';

import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { SurveyProgressBanner } from '@/components/survey/SurveyProgressBanner';

import { APP_PAGE_BACKGROUND } from './brandSurface';
import Navbar from './navbar';
import {
  Sidebar,
  drawerClosedWidth,
  drawerWidth,
  useSidebar,
} from './sidebar';

/**
 * Authenticated app shell (sidebar + navbar + gutters).
 *
 * Page contract:
 * - Prefer PageHeader for title / subtitle / actions / breadcrumbs.
 * - Do not nest another Container with horizontal px, or re-add root padding —
 *   gutters already come from this layout.
 */
export default function MainLayout({
  children,
  noGutters = false,
  backgroundColor,
}: {
  children?: ReactNode;
  noGutters?: boolean;
  backgroundColor?: string;
}): React.JSX.Element {
  return (
    <Box sx={{ display: 'flex' }}>
      <Navbar />
      <Box
        component='nav'
        sx={{
          flexShrink: { sm: 0 },
          position: 'fixed',
        }}
        aria-label='sidebar mobile'
      >
        <Sidebar />
      </Box>
      <MainContent noGutters={noGutters} backgroundColor={backgroundColor}>
        {children}
      </MainContent>
    </Box>
  );
}

function MainContent({
  children,
  noGutters,
  backgroundColor,
}: {
  children?: ReactNode;
  noGutters?: boolean;
  backgroundColor?: string;
}): React.JSX.Element {
  const isOpen = useSidebar().isOpen;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      component='main'
      sx={{
        flexGrow: 1,
        width: isMobile ? '100%' : `calc(100% - ${drawerWidth}px)`,
        mt: isMobile ? '56px' : '0',
        pt: noGutters ? '0' : '16px',
        backgroundColor: backgroundColor || APP_PAGE_BACKGROUND,
        marginLeft: isMobile
          ? 0
          : isOpen
            ? `${drawerWidth}px`
            : `${drawerClosedWidth}px`,
        transition: 'margin-left 0.3s',
        minHeight: isMobile ? 'calc(100vh - 56px)' : '100vh',
      }}
    >
      <SurveyProgressBanner />
      <Container
        maxWidth={false}
        disableGutters={noGutters}
        sx={noGutters ? undefined : { px: { xs: 2, sm: 3 } }}
      >
        {children}
      </Container>
    </Box>
  );
}
